import { useState } from 'react'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { useFittingItems } from '@/hooks/element-hooks'
import { SearchField } from '@/components/SearchField'
import styles from './JobSidebarHeader.module.css'

export interface JobSidebarHeaderSavedFilter {
  id: string
  name: string
}

export interface JobSidebarHeaderProps {
  searchText: string
  onSearch: (searchText: string) => void
  savedFilters: JobSidebarHeaderSavedFilter[]
  selectedSavedFilterId: string | null
  onSelectSavedFilter: (id: string) => void
}

export function JobSidebarHeader({
  searchText,
  onSearch,
  savedFilters,
  selectedSavedFilterId,
  onSelectSavedFilter,
}: JobSidebarHeaderProps) {
  const [searching, setSearching] = useState(Boolean(searchText))
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const selectedIndex = savedFilters.findIndex(
    (f) => f.id === selectedSavedFilterId,
  )
  const { rowRef, measureRef, shown, folded } = useFittingItems(
    savedFilters,
    selectedIndex,
  )

  return (
    <div className={styles.root}>
      <div className={styles.titleRow}>
        <span className={styles.title}>My jobs</span>
        <RawButton
          className={styles.iconButton}
          aria-label="Search jobs"
          aria-pressed={searching}
          onPress={() => {
            if (searching) {
              onSearch('')
            }
            setSearching(!searching)
          }}
        >
          <Icon name="search" size={18} aria-hidden="true" />
        </RawButton>
      </div>
      {searching && (
        <SearchField
          size="small"
          stretch
          autoFocus
          value={searchText}
          onChange={onSearch}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onSearch('')
              setSearching(false)
            }
          }}
          aria-label="Search jobs or clients"
          placeholder="Search jobs or clients"
          testId="job-list-search"
        />
      )}
      <div className={styles.tabRow} ref={rowRef}>
        <div className={styles.tabs} role="tablist" aria-label="Saved filters">
          {shown.map((saved) => (
            <RawButton
              key={saved.id}
              role="tab"
              aria-selected={selectedSavedFilterId === saved.id}
              className={styles.tab}
              onPress={() => onSelectSavedFilter(saved.id)}
            >
              {saved.name}
            </RawButton>
          ))}
          {folded.length > 0 && (
            <Dropdown
              isOpen={isMoreOpen}
              onButtonPress={() => setIsMoreOpen(!isMoreOpen)}
              onRequestClose={() => setIsMoreOpen(false)}
              position="bottom-start"
              compact
              button={
                <RawButton
                  className={styles.tab}
                  aria-haspopup="menu"
                  aria-expanded={isMoreOpen}
                >
                  More <span className={styles.moreCount}>{folded.length}</span>
                  <Icon name="chevron-down" size={14} />
                </RawButton>
              }
            >
              {folded.map((saved) => (
                <DropdownItem
                  key={saved.id}
                  onPress={() => {
                    setIsMoreOpen(false)
                    onSelectSavedFilter(saved.id)
                  }}
                >
                  {saved.name}
                </DropdownItem>
              ))}
            </Dropdown>
          )}
        </div>
        <div className={styles.measure} ref={measureRef} aria-hidden="true">
          {savedFilters.map((saved) => (
            <span key={saved.id} className={styles.tab}>
              {saved.name}
            </span>
          ))}
          <span className={styles.tab}>
            More 00
            <Icon name="chevron-down" size={14} />
          </span>
        </div>
      </div>
    </div>
  )
}
